import { useCallback, useEffect, useState } from 'react';
import AuthService from '../../services/AuthService';
import WorkspaceSettingsService from '../../services/WorkspaceSettingsService';
import ClientService from '../../../SuperAdmin/services/ClientService';

const NOTIFICATION_STORAGE_KEY = 'nf_workspace_notification_prefs_v1';

const card = {
  background: '#fff',
  borderRadius: 16,
  boxShadow: '0 4px 24px rgba(0,0,0,.08)',
  padding: 28,
};

function loadNotificationPrefs() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    if (!raw)
      return { dueFollowUpAlerts: true, overdueBadge: true, soundHints: false };
    const o = JSON.parse(raw);
    return {
      dueFollowUpAlerts: o.dueFollowUpAlerts !== false,
      overdueBadge: o.overdueBadge !== false,
      soundHints: !!o.soundHints,
    };
  } catch {
    return { dueFollowUpAlerts: true, overdueBadge: true, soundHints: false };
  }
}

function mondayISO(d = new Date()) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x.toISOString().slice(0, 10);
}

export default function WorkspaceSettings() {
  const loggedInClientId = AuthService.getLoggedInClientId();
  const roleNum = parseInt(AuthService.getLoggedInRoleId(), 10);
  const isSuperAdmin = roleNum === 1;
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const clientId = isSuperAdmin
    ? (selectedAgencyId ? parseInt(selectedAgencyId, 10) : null)
    : loggedInClientId;
  const canEditAttendanceRules = [1, 2, 3].includes(roleNum);

  const [tab, setTab] = useState('notifications');
  const [toast, setToast] = useState(null);
  const [notif, setNotif] = useState(loadNotificationPrefs);

  const [fence, setFence] = useState({
    officeLatitude: '',
    officeLongitude: '',
    radiusMeters: '',
  });
  const [fenceLoading, setFenceLoading] = useState(false);

  const showMsg = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const list = await ClientService.getAllCLients();
        setAgencies(Array.isArray(list) ? list : []);
      } catch {
        showMsg('Could not load agency list.', 'error');
      }
    })();
  }, [isSuperAdmin]);

  const persistNotif = (next) => {
    setNotif(next);
    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(next));
    showMsg('Notification preferences saved on this device.');
  };

  const loadFence = useCallback(async () => {
    if (!clientId) return;
    setFenceLoading(true);
    try {
      const data = await WorkspaceSettingsService.getAttendanceGeoFence(clientId);
      setFence({
        officeLatitude:
          data.officeLatitude != null ? String(data.officeLatitude) : '',
        officeLongitude:
          data.officeLongitude != null ? String(data.officeLongitude) : '',
        radiusMeters:
          data.radiusMeters != null && data.radiusMeters > 0
            ? String(data.radiusMeters)
            : '',
      });
    } catch {
      showMsg('Could not load attendance area settings.', 'error');
    } finally {
      setFenceLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (tab === 'attendance' && clientId) loadFence();
  }, [tab, clientId, loadFence]);

  const saveFence = async () => {
    if (!clientId || !canEditAttendanceRules) return;
    const lat = fence.officeLatitude.trim() === '' ? null : Number(fence.officeLatitude);
    const lng = fence.officeLongitude.trim() === '' ? null : Number(fence.officeLongitude);
    const r =
      fence.radiusMeters.trim() === '' ? null : parseInt(fence.radiusMeters, 10);
    if (lat != null && (Number.isNaN(lat) || lat < -90 || lat > 90)) {
      showMsg('Latitude must be between -90 and 90.', 'error');
      return;
    }
    if (lng != null && (Number.isNaN(lng) || lng < -180 || lng > 180)) {
      showMsg('Longitude must be between -180 and 180.', 'error');
      return;
    }
    if (r != null && (Number.isNaN(r) || r <= 0)) {
      showMsg('Radius must be a positive number of meters, or leave empty to disable.', 'error');
      return;
    }
    try {
      await WorkspaceSettingsService.updateAttendanceGeoFence(clientId, {
        officeLatitude: lat,
        officeLongitude: lng,
        radiusMeters: r,
      });
      showMsg('Attendance area saved. Employees must check in within this radius.');
      loadFence();
    } catch {
      showMsg('Save failed. You may need Admin-level permissions.', 'error');
    }
  };

  const disableFence = async () => {
    setFence({ officeLatitude: '', officeLongitude: '', radiusMeters: '' });
    if (!clientId || !canEditAttendanceRules) return;
    try {
      await WorkspaceSettingsService.updateAttendanceGeoFence(clientId, {
        officeLatitude: null,
        officeLongitude: null,
        radiusMeters: null,
      });
      showMsg('Attendance geo rule disabled.');
      loadFence();
    } catch {
      showMsg('Could not disable rule.', 'error');
    }
  };

  if (!clientId && !isSuperAdmin) {
    return (
      <div style={{ padding: 48, color: '#8392ab' }}>
        Workspace settings require a tenant account with a client assignment.
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: '#f4f7fa', minHeight: '100vh' }}>
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            background: toast.type === 'error' ? '#f5365c' : '#2dce89',
            color: '#fff',
            padding: '14px 28px',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,.2)',
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {toast.msg}
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1a1f36' }}>
          <i className="fas fa-sliders-h" style={{ color: '#5e72e4', marginRight: 10 }} />
          Workspace settings
        </h3>
        <p style={{ margin: '8px 0 0', color: '#8898aa', fontSize: 14 }}>
          {isSuperAdmin
            ? 'Choose an agency to manage its workspace rules. Notification preferences are stored on this browser.'
            : 'Notifications are stored on this browser. Attendance area applies when employees check in or out.'}
        </p>
      </div>

      {isSuperAdmin && (
        <div style={{ ...card, marginBottom: 20, maxWidth: 520 }}>
          <h4 style={{ marginTop: 0, color: '#344767' }}>Agency / client</h4>
          <p style={{ color: '#8898aa', fontSize: 13, marginBottom: 12 }}>
            Select which tenant workspace you want to configure.
          </p>
          <select
            value={selectedAgencyId}
            onChange={(e) => setSelectedAgencyId(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid #e9ecef',
              fontWeight: 600,
              color: '#344767',
            }}
          >
            <option value="">Select agency…</option>
            {agencies.map((agency) => (
              <option key={agency.id} value={agency.id}>
                {agency.agencyName || agency.clientName || `Client #${agency.id}`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 4,
          marginBottom: 20,
          background: '#fff',
          borderRadius: 12,
          padding: 5,
          width: 'fit-content',
          boxShadow: '0 2px 8px rgba(0,0,0,.06)',
        }}
      >
        {[
          ['notifications', 'fas fa-bell', 'Notifications'],
          ['attendance', 'fas fa-map-marker-alt', 'Attendance area'],
        ].map(([key, icon, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 14,
              background: tab === key ? '#5e72e4' : 'transparent',
              color: tab === key ? '#fff' : '#8898aa',
            }}
          >
            <i className={icon} style={{ marginRight: 7 }} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'notifications' && (
        <div style={card}>
          <h4 style={{ marginTop: 0, color: '#344767' }}>In-app behaviour</h4>
          <p style={{ color: '#8898aa', fontSize: 13, marginBottom: 20 }}>
            These preferences apply only on this device (local storage).
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <input
              type="checkbox"
              checked={notif.dueFollowUpAlerts}
              onChange={(e) =>
                persistNotif({ ...notif, dueFollowUpAlerts: e.target.checked })
              }
            />
            <span style={{ fontWeight: 600, color: '#344767' }}>Highlight due follow-ups in navigation</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <input
              type="checkbox"
              checked={notif.overdueBadge}
              onChange={(e) =>
                persistNotif({ ...notif, overdueBadge: e.target.checked })
              }
            />
            <span style={{ fontWeight: 600, color: '#344767' }}>Show overdue counts on the menu badge</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="checkbox"
              checked={notif.soundHints}
              onChange={(e) =>
                persistNotif({ ...notif, soundHints: e.target.checked })
              }
            />
            <span style={{ fontWeight: 600, color: '#344767' }}>
              Allow subtle UI sounds for confirmations (when supported)
            </span>
          </label>
        </div>
      )}

      {tab === 'attendance' && (
        <div style={card}>
          {isSuperAdmin && !clientId && (
            <p style={{ color: '#8898aa' }}>
              Select an agency above to view or edit attendance geo-fence settings.
            </p>
          )}
          {clientId && (
            <>
              <h4 style={{ marginTop: 0, color: '#344767' }}>Office geo-fence</h4>
              <p style={{ color: '#8898aa', fontSize: 13, lineHeight: 1.6 }}>
                When latitude, longitude and a radius (meters) are set, employees must share GPS on check-in/check-out and
                must be within that distance of the office point. Otherwise they see{' '}
                <strong>&quot;Attendance area is out of office&quot;</strong> from the server.
              </p>
              {!canEditAttendanceRules && (
                <div
                  style={{
                    padding: 12,
                    background: '#fff8e6',
                    borderRadius: 10,
                    marginBottom: 16,
                    fontSize: 13,
                    color: '#856404',
                  }}
                >
                  Only Super Admin or Admin can change these values (legacy JWT role 3 is still honored).
                </div>
              )}
              {fenceLoading ? (
                <p style={{ color: '#8898aa' }}>
                  <i className="fas fa-circle-notch fa-spin me-2" /> Loading…
                </p>
              ) : (
                <div style={{ maxWidth: 480 }}>
                  <label style={{ display: 'block', marginBottom: 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#8392ab' }}>
                      OFFICE LATITUDE (WGS84)
                    </span>
                    <input
                      style={{
                        width: '100%',
                        marginTop: 6,
                        padding: '10px 14px',
                        borderRadius: 10,
                        border: '1px solid #e9ecef',
                      }}
                      disabled={!canEditAttendanceRules}
                      placeholder="e.g. 28.6139"
                      value={fence.officeLatitude}
                      onChange={(e) =>
                        setFence((f) => ({ ...f, officeLatitude: e.target.value }))
                      }
                    />
                  </label>
                  <label style={{ display: 'block', marginBottom: 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#8392ab' }}>
                      OFFICE LONGITUDE
                    </span>
                    <input
                      style={{
                        width: '100%',
                        marginTop: 6,
                        padding: '10px 14px',
                        borderRadius: 10,
                        border: '1px solid #e9ecef',
                      }}
                      disabled={!canEditAttendanceRules}
                      placeholder="e.g. 77.2090"
                      value={fence.officeLongitude}
                      onChange={(e) =>
                        setFence((f) => ({ ...f, officeLongitude: e.target.value }))
                      }
                    />
                  </label>
                  <label style={{ display: 'block', marginBottom: 16 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#8392ab' }}>
                      RADIUS (meters)
                    </span>
                    <input
                      style={{
                        width: '100%',
                        marginTop: 6,
                        padding: '10px 14px',
                        borderRadius: 10,
                        border: '1px solid #e9ecef',
                      }}
                      disabled={!canEditAttendanceRules}
                      placeholder="e.g. 200 — leave empty to disable"
                      value={fence.radiusMeters}
                      onChange={(e) =>
                        setFence((f) => ({ ...f, radiusMeters: e.target.value }))
                      }
                    />
                  </label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      disabled={!canEditAttendanceRules}
                      onClick={saveFence}
                      style={{
                        padding: '10px 22px',
                        borderRadius: 10,
                        border: 'none',
                        background: '#5e72e4',
                        color: '#fff',
                        fontWeight: 700,
                        cursor: canEditAttendanceRules ? 'pointer' : 'not-allowed',
                        opacity: canEditAttendanceRules ? 1 : 0.5,
                      }}
                    >
                      Save attendance rule
                    </button>
                    <button
                      type="button"
                      disabled={!canEditAttendanceRules}
                      onClick={disableFence}
                      style={{
                        padding: '10px 22px',
                        borderRadius: 10,
                        border: '1px solid #e9ecef',
                        background: '#fff',
                        fontWeight: 700,
                        cursor: canEditAttendanceRules ? 'pointer' : 'not-allowed',
                        opacity: canEditAttendanceRules ? 1 : 0.5,
                      }}
                    >
                      Disable geo rule
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!navigator.geolocation) {
                          showMsg('Geolocation not supported in this browser.', 'error');
                          return;
                        }
                        navigator.geolocation.getCurrentPosition(
                          (p) => {
                            setFence((f) => ({
                              ...f,
                              officeLatitude: String(p.coords.latitude.toFixed(7)),
                              officeLongitude: String(p.coords.longitude.toFixed(7)),
                            }));
                            showMsg('Filled coordinates from this device’s location.');
                          },
                          () => showMsg('Could not read GPS. Allow location access.', 'error'),
                        );
                      }}
                      disabled={!canEditAttendanceRules}
                      style={{
                        padding: '10px 22px',
                        borderRadius: 10,
                        border: '1px solid #5e72e4',
                        background: '#fff',
                        color: '#5e72e4',
                        fontWeight: 700,
                        cursor: canEditAttendanceRules ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Use my current location as office
                    </button>
                  </div>
                  <p style={{ marginTop: 16, fontSize: 12, color: '#adb5bd' }}>
                    Tip: default search “your building” in Google Maps → right-click → coordinates.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
